import type { ChangeEvent, DragEvent, FormEvent, SyntheticEvent } from 'react';
import { useState, useRef, useCallback } from 'react';
import {
  Camera,
  Image as ImageIcon,
  Loader2,
  Copy,
  Check,
  AlertCircle,
  RotateCcw,
  Download,
  Languages,
  AlertTriangle,
} from 'lucide-react';
import { Button, Select } from '../components/ui';
import type { LanguageCode, Register, OCRResponse } from '@zolotalk/shared-types';
import { apiClient } from '../api/client';
import './OCRTranslatePage.css';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'ru', label: 'Русский' },
];

const REGISTER_OPTIONS = [
  { value: 'auto', label: 'Auto (detect from context)' },
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
];

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

export function OCRTranslatePage() {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [targetLanguage, setTargetLanguage] = useState<LanguageCode>('es');
  const [register, setRegister] = useState<Register>('auto');
  const [extractedText, setExtractedText] = useState<string>('');
  const [translatedText, setTranslatedText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<number | null>(null);
  const [copied, setCopied] = useState<'extracted' | 'translation' | null>(null);
  const [showExtracted, setShowExtracted] = useState(false);
  const [lowConfidenceRegions, setLowConfidenceRegions] = useState<
    NonNullable<OCRResponse['lowConfidenceRegions']>
  >([]);
  const [retakeRecommended, setRetakeRecommended] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError('Unsupported file type. Please upload JPEG, PNG, WebP, or HEIC images.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit.`);
      return;
    }

    setError(null);
    setImageFile(file);
    setExtractedText('');
    setTranslatedText('');
    setConfidence(null);
    setLowConfidenceRegions([]);
    setRetakeRecommended(false);
    setCopied(null);

    const reader = new FileReader();
    reader.onload = e => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const clearImage = useCallback(() => {
    setImageFile(null);
    setImagePreview(null);
    setExtractedText('');
    setTranslatedText('');
    setConfidence(null);
    setLowConfidenceRegions([]);
    setRetakeRecommended(false);
    setCopied(null);
    setError(null);
    setImageDimensions({ width: 0, height: 0 });
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (uploadInputRef.current) uploadInputRef.current.value = '';
  }, []);

  const handleTranslate = async (e: FormEvent) => {
    e.preventDefault();
    if (!imageFile) {
      setError('Please select an image first.');
      return;
    }

    setLoading(true);
    setError(null);
    setCopied(null);

    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      formData.append('targetLanguage', targetLanguage);
      formData.append('register', register);

      const data = await apiClient.post<OCRResponse>('/api/ocr', formData);
      setExtractedText(data.extractedText);
      setTranslatedText(data.translatedText);
      setConfidence(data.confidence);
      setLowConfidenceRegions(data.lowConfidenceRegions ?? []);
      setRetakeRecommended(data.retakeRecommended ?? false);
    } catch {
      setError('OCR translation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyExtracted = async () => {
    if (extractedText) {
      await navigator.clipboard.writeText(extractedText);
      setCopied('extracted');
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const handleCopyTranslation = async () => {
    if (translatedText) {
      await navigator.clipboard.writeText(translatedText);
      setCopied('translation');
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const handleDownload = () => {
    if (!extractedText && !translatedText) return;
    const content = `Original Text:\n${extractedText}\n\nTranslation (${targetLanguage}):\n${translatedText}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zolotalk-ocr-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRetake = () => {
    clearImage();
    cameraInputRef.current?.click();
  };

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    setImageDimensions({
      width: event.currentTarget.naturalWidth,
      height: event.currentTarget.naturalHeight,
    });
  };

  const lowConfidence =
    (confidence !== null && confidence < 0.7) || lowConfidenceRegions.length > 0;

  return (
    <div className="page ocr-translate-page">
      <div className="container">
        <header className="page-header">
          <h1>Camera Translation</h1>
          <p className="page-description">
            Capture or upload an image to extract and translate text instantly.
          </p>
        </header>

        {error && (
          <div className="error-banner" role="alert">
            <AlertCircle size={18} aria-hidden="true" />
            {error}
            <button
              type="button"
              className="error-dismiss"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              <RotateCcw size={16} />
            </button>
          </div>
        )}

        {imagePreview ? (
          <>
            <div className="image-preview-wrapper">
              <div className="image-canvas">
                <img
                  src={imagePreview}
                  alt="Document selected for translation"
                  className="image-preview"
                  onLoad={handleImageLoad}
                />
                {lowConfidenceRegions.length > 0 && imageDimensions.width > 0 && (
                  <svg
                    className="confidence-overlay"
                    viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
                    role="img"
                    aria-label={`${lowConfidenceRegions.length} low-confidence ${
                      lowConfidenceRegions.length === 1 ? 'region' : 'regions'
                    } highlighted`}
                  >
                    {lowConfidenceRegions.map((region, index) => (
                      <rect
                        key={`${region.x}-${region.y}-${index}`}
                        x={region.x}
                        y={region.y}
                        width={region.width}
                        height={region.height}
                      />
                    ))}
                  </svg>
                )}
              </div>
              <div className="image-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRetake}
                  aria-label="Retake or choose another image"
                >
                  <RotateCcw size={16} />
                  Retake
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDownload}
                  disabled={!extractedText && !translatedText}
                  aria-label="Download translation as text file"
                >
                  <Download size={16} />
                  Download
                </Button>
              </div>
            </div>

            <form onSubmit={handleTranslate} className="translate-form">
              <Select
                id="target-language"
                label="Target Language"
                value={targetLanguage}
                onChange={e => setTargetLanguage(e.target.value as LanguageCode)}
                options={LANGUAGE_OPTIONS}
              />

              <Select
                id="register"
                label="Register / Tone"
                value={register}
                onChange={e => setRegister(e.target.value as Register)}
                options={REGISTER_OPTIONS}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={loading}
                className="translate-btn"
                disabled={!imageFile}
              >
                {loading ? <Loader2 size={20} /> : 'Extract & Translate'}
              </Button>
            </form>

            {loading && (
              <div className="results-skeleton" role="status" aria-live="polite">
                <span className="sr-only">Extracting and translating document</span>
                <div className="skeleton-line skeleton-line-short" />
                <div className="skeleton-line" />
                <div className="skeleton-line" />
              </div>
            )}

            {(extractedText || translatedText) && (
              <section className="results-section" aria-live="polite">
                <p className="sr-only">Document translation complete</p>
                {retakeRecommended && (
                  <div className="retake-notice" role="alert">
                    <AlertTriangle size={18} aria-hidden="true" />
                    <span>This image is too unclear to translate reliably. Retake the photo.</span>
                    <Button variant="secondary" size="sm" onClick={handleRetake}>
                      Retake photo
                    </Button>
                  </div>
                )}
                <div className="results-header">
                  <h2>Results</h2>
                  <div className="results-meta">
                    {confidence !== null && (
                      <span
                        className={`confidence-badge ${lowConfidence ? 'low' : ''}`}
                        aria-label={`Confidence: ${Math.round(confidence * 100)}%`}
                      >
                        {lowConfidence && <AlertTriangle size={14} aria-hidden="true" />}
                        {Math.round(confidence * 100)}% confidence
                      </span>
                    )}
                    <p className="result-lang">
                      → {LANGUAGE_OPTIONS.find(l => l.value === targetLanguage)?.label} •
                      {register === 'auto' ? 'Auto register' : register}
                    </p>
                  </div>
                </div>

                <div className="results-tabs" role="tablist" aria-label="Document results">
                  <button
                    type="button"
                    className={`results-tab ${showExtracted ? 'active' : ''}`}
                    onClick={() => setShowExtracted(true)}
                    role="tab"
                    aria-selected={showExtracted}
                  >
                    <ImageIcon size={16} aria-hidden="true" />
                    Extracted Text
                  </button>
                  <button
                    type="button"
                    className={`results-tab ${!showExtracted ? 'active' : ''}`}
                    onClick={() => setShowExtracted(false)}
                    role="tab"
                    aria-selected={!showExtracted}
                  >
                    <Languages size={16} aria-hidden="true" />
                    Translation
                  </button>
                </div>

                <div className="results-content" role="tabpanel">
                  {showExtracted ? (
                    <div className="result-panel">
                      <div className="result-panel-header">
                        <h3>Extracted Text</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCopyExtracted}
                          aria-label={copied === 'extracted' ? 'Copied' : 'Copy extracted text'}
                        >
                          {copied === 'extracted' ? <Check size={16} /> : <Copy size={16} />}
                        </Button>
                      </div>
                      <pre className="result-text extracted">
                        {extractedText || 'No text detected'}
                      </pre>
                      {lowConfidenceRegions.length > 0 && (
                        <p className="low-confidence-notice" role="status">
                          <AlertTriangle size={16} aria-hidden="true" />
                          {lowConfidenceRegions.length} low-confidence{' '}
                          {lowConfidenceRegions.length === 1 ? 'region is' : 'regions are'}
                          {' highlighted on the image. Extracted text may contain errors.'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="result-panel">
                      <div className="result-panel-header">
                        <h3>Translation</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCopyTranslation}
                          aria-label={copied === 'translation' ? 'Copied' : 'Copy translation'}
                        >
                          {copied === 'translation' ? <Check size={16} /> : <Copy size={16} />}
                        </Button>
                      </div>
                      <div className="result-text translation">
                        {translatedText || 'No translation available'}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}
          </>
        ) : (
          <div
            className="drop-zone"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={cameraInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              capture="environment"
              onChange={handleFileInputChange}
              className="file-input"
              aria-label="Capture document with camera"
            />
            <input
              ref={uploadInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              onChange={handleFileInputChange}
              className="file-input"
              aria-label="Upload document image"
            />
            <div className="drop-zone-content">
              <Camera size={48} aria-hidden="true" />
              <h2>Drop image here or choose a capture option</h2>
              <p className="drop-zone-hint">Supports JPEG, PNG, WebP, HEIC · Max 20MB</p>
              <div className="capture-actions">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={event => {
                    event.stopPropagation();
                    cameraInputRef.current?.click();
                  }}
                >
                  <Camera size={18} aria-hidden="true" />
                  Take Photo
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={event => {
                    event.stopPropagation();
                    uploadInputRef.current?.click();
                  }}
                >
                  <ImageIcon size={18} aria-hidden="true" />
                  Choose File
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
